import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { useEffect, useState } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import loginbg from "../../assets/images/login.gif";
import esurveylogonew from "../../assets/images/logo.png";
import ESInput from "../../controls/ESInput";
import { organizationInsertForSignUp } from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import { ToastTypeCode, showToast } from "../../utils/Constant";
import Loader from "../loader/Loader";
import "../login/signup.scss";
import { AppConfig } from "../../AppConfig";
import CloudflareTurnstile from "./CloudflareTurnstile";

interface SignupUser {
  organizationName: string;
  firstName: string;
  lastName: string;
  password: string;
  email: string;
  contactNumber: number | null;
  countryCode: number | null;
  country?: string;
  token?: string;
  tenentId?: string;
  isActive?: boolean;
  planId?: number;
}

interface Option {
  label: string;
  value: string;
}

countries.registerLocale(enLocale);

const getCountryOptions = () => {
  const countryObj = countries?.getNames("en", { select: "official" });
  return Object.entries(countryObj)?.map(([code, name]) => ({
    value: code,
    label: `${getFlagEmoji(code)} ${name}`,
  }));
};

function getFlagEmoji(countryCode: string) {
  const codePoints = countryCode
    ?.toUpperCase()
    ?.split("")
    ?.map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

const Signup = () => {
  const methods = useForm<SignupUser>({
    defaultValues: {
      organizationName: "",
      firstName: "",
      lastName: "",
      password: "",
      email: "",
      contactNumber: null,
      countryCode: null,
      isActive: true,
      country: "",
    },
  });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { planId, currencyMode } = useParams();
  const [countries, setCountries] = useState<Option[]>([]);
  const [token, setToken] = useState("");
  const checkCaptcha = AppConfig.CLOUDFLARE_CHECK;

  useEffect(() => {
    const countryOptions = getCountryOptions();
    setCountries(countryOptions);
  }, []);

  const onSubmit: SubmitHandler<SignupUser> = async (data) => {
    if(checkCaptcha){
      if (!token) {
        showToast("Please verify the captcha", ToastTypeCode.ERROR);
        return;
      }
    }
    const transformedData = {
      ...data,
      isActive: data.isActive ? 1 : 0,
      planId: planId || null,
      plan_currency: currencyMode || "INR",
      country: data?.country?.split(" ")[0] || "IN",
      CaptchaToken: token
    };
    setLoading(true);
    try {
      const response: any = await axiosPost(
        organizationInsertForSignUp,
        transformedData
      );

      if (response?.data?.Status === 200) {
        showToast("Signup successful", ToastTypeCode?.SUCCESS);
        navigate("/login");
      } else if (response?.data?.Errors?.length > 0) {
        showToast(response?.data?.Errors[0]?.Message, ToastTypeCode?.ERROR);
      } else if (response?.data?.ErrorMessage != null) {
        showToast(response?.data?.ErrorMessage, ToastTypeCode?.ERROR);
      } else {
        showToast("Signup unsuccessful", ToastTypeCode?.ERROR);
      }
    } catch (error: any) {
      if (error?.response?.data?.Errors?.length > 0) {
        showToast(error.response.data.Errors[0].Message, ToastTypeCode?.ERROR);
      } else {
        showToast("Signup unsuccessful", ToastTypeCode?.ERROR);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Container fluid className="form">
        <div className="main-content">
          <Row>
            <Col lg="6" className="d-block m-auto login-bg">
              <div className="login-card">
                <img src={esurveylogonew} className="esurveylogo" alt="logo" />
                  <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                      <div className="form-sec">
                        <Form.Group as={Col} className="mb-1">
                          <Form.Label>
                            Organization Name{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <ESInput
                            className={"form-control"}
                            name={"organizationName"}
                            placeholder={"Organization Name"}
                            type={"text"}
                            required={true}
                          />
                        </Form.Group>

                        <Row>
                          <Form.Group as={Col} md={6} className="mb-1">
                            <Form.Label>
                              First Name <span className="text-danger">*</span>
                            </Form.Label>
                            <ESInput
                              className={"form-control"}
                              name={"firstName"}
                              placeholder={"First Name"}
                              type={"text"}
                              required={true}
                            />
                          </Form.Group>

                          <Form.Group as={Col} md={6} className="mb-1">
                            <Form.Label>
                              Last Name <span className="text-danger">*</span>
                            </Form.Label>
                            <ESInput
                              className={"form-control"}
                              name={"lastName"}
                              placeholder={"Last Name"}
                              type={"text"}
                              required={true}
                            />
                          </Form.Group>
                        </Row>

                        <Form.Group as={Col} className="mb-1">
                          <Form.Label>
                            Email <span className="text-danger">*</span>
                          </Form.Label>
                          <ESInput
                            className={"form-control"}
                            name={"email"}
                            placeholder={"Email"}
                            type={"text"}
                            required={true}
                            regex={/^[\w-\.]+@([\w-]+\.)+[\w-]{2,5}$/}
                          />
                        </Form.Group>

                        <Form.Group as={Col} className="mb-1">
                          <Form.Label>
                            Password <span className="text-danger">*</span>
                          </Form.Label>
                          <ESInput
                            className={"form-control"}
                            name={"password"}
                            placeholder={"SignUp Password"}
                            type={"password"}
                            minLength={8}
                            required={true}
                            showEyeIcon={true}
                          />
                        </Form.Group>

                        <Row className="mb-1">
                          <Form.Group as={Col} md={6}>
                            <Form.Label>
                              Country Code{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <ESInput
                              className={"form-control"}
                              name={"countryCode"}
                              placeholder={"Country Code"}
                              type="tel"
                              required={true}
                            />
                          </Form.Group>

                          <Form.Group as={Col} md={6}>
                            <Form.Label>
                              Contact Number{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <ESInput
                              className={"form-control"}
                              name={"contactNumber"}
                              placeholder={"Contact Number"}
                              type="tel"
                              required={true}
                              regex={/^[0-9]{10}$/}
                              isNumber={true}
                            />
                          </Form.Group>
                        </Row>

                        <Row>
                          <Form.Group as={Col} md={12} className="mb-1">
                            <Form.Label>
                              Country <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                              name="country"
                              control={methods?.control}
                              rules={{ required: "Country is required" }}
                              render={({ field, fieldState }) => {
                                const selectedOption = countries?.find(
                                  (c) => c?.value === field?.value
                                ) || null;
                                return (
                                  <>
                                    <Select<Option>
                                      options={countries}
                                      isSearchable
                                      placeholder="Select Country"
                                      onChange={(selected) => field?.onChange(selected?.value || "")}
                                      onBlur={field?.onBlur}
                                      value={selectedOption}
                                    />
                                    {fieldState?.error?.message && (
                                      <p className="text-danger">{fieldState?.error?.message}</p>
                                    )}
                                  </>
                                );
                              }}
                            />
                          </Form.Group>  
                        </Row>
                        {(checkCaptcha) && (
                          <div className="mt-3">
                            <CloudflareTurnstile
                                size="normal"
                                onVerify={(token) => {
                                  setToken(token);
                                }}
                            />
                          </div>
                        )}
                        <Col md={12}>
                          <input
                            type="submit"
                            className="login-btn w-100"
                            value="Sign Up"
                          />
                        </Col>
                      </div>
                    </form>
                  </FormProvider>
                <Row>
                  <div className="col-md-12 text-center">
                    <button
                      onClick={() => navigate("/login")}
                      className="back-text mt-2"
                    >
                      Already have an account? <span style={{
                              color: "#2ca9b8",
                              cursor: "pointer",
                              textDecoration: "none",
                            }}>Login</span>
                    </button>
                  </div>
                </Row>
              </div>
            </Col>
            <Col lg="6" className="px-0 login-bgimg">
               <div className="login-bgimg">
               <div className="login-card signup-card">
                 <img src={loginbg} alt="Login" title="login" className="" />
                <p>Connect with Surveysides…</p>
               </div>
              </div>
            </Col>
          </Row>
        </div>
      </Container>
      {loading && <Loader />}
    </div>
  );
};

export default Signup;
